import { posPackageTypeLabel } from '@/lib/pos/posPackage'

export const PACKAGE_TYPES = [
  'fixed_combo',
  'flexible_combo',
  'prepaid_wallet',
  'membership',
  'gift_voucher',
  'session_pack',
]

export function emptyPackageForm(type = 'flexible_combo') {
  return {
    name: '',
    type,
    description: '',
    sale_price: '',
    validity_days: '',
    is_transferable: false,
    sellable_on_pos: true,
    is_active: true,
    sort: 0,
    rule: emptyRuleForm(),
    allowed_services: [],
  }
}

export function emptyRuleForm() {
  return {
    pick_count: 3,
    credit_amount: '',
    session_count: 10,
    membership_period_days: 30,
    rollover_unused: false,
    redeem_products: false,
  }
}

/**
 * @param {import('@/types/api').MasterPackage} pkg
 */
export function packageToForm(pkg) {
  return {
    name: pkg.name ?? '',
    type: pkg.type ?? 'flexible_combo',
    description: pkg.description ?? '',
    sale_price: toAmount(pkg.sale_price),
    validity_days: pkg.validity_days ?? '',
    is_transferable: Boolean(pkg.is_transferable),
    sellable_on_pos: Boolean(pkg.sellable_on_pos ?? true),
    is_active: Boolean(pkg.is_active ?? true),
    sort: pkg.sort ?? 0,
    rule: {
      pick_count: pkg.rule?.pick_count ?? 3,
      credit_amount: toAmount(pkg.rule?.credit_amount),
      session_count: pkg.rule?.session_count ?? 10,
      membership_period_days: pkg.rule?.membership_period_days ?? 30,
      rollover_unused: Boolean(pkg.rule?.rollover_unused),
      redeem_products: Boolean(pkg.rule?.redeem_products),
    },
    allowed_services: (pkg.allowed_services ?? []).map((row, index) => ({
      service_id: row.service_id,
      service_name: row.service_name ?? '',
      quantity: row.quantity ?? 1,
      sort: row.sort ?? (index + 1) * 10,
    })),
  }
}

/**
 * @param {ReturnType<typeof emptyPackageForm>} form
 */
export function formToPayload(form) {
  const payload = {
    name: form.name,
    type: form.type,
    description: form.description || null,
    sale_price: toAmount(form.sale_price),
    validity_days: form.validity_days === '' ? null : Number(form.validity_days),
    is_transferable: form.is_transferable,
    sellable_on_pos: form.sellable_on_pos,
    is_active: form.is_active,
    sort: Number(form.sort) || 0,
    rule: buildRulePayload(form),
    allowed_services: buildAllowedServicesPayload(form),
  }

  return payload
}

function buildRulePayload(form) {
  const rule = { ...form.rule }

  switch (form.type) {
    case 'flexible_combo':
      return { pick_count: Number(rule.pick_count) || 2 }
    case 'prepaid_wallet':
      return { credit_amount: toAmount(rule.credit_amount || form.sale_price) }
    case 'session_pack':
      return { session_count: Number(rule.session_count) || 1 }
    case 'membership':
      return {
        membership_period_days: Number(rule.membership_period_days) || 30,
        rollover_unused: Boolean(rule.rollover_unused),
      }
    case 'gift_voucher':
      return {
        credit_amount: toAmount(form.sale_price),
        redeem_products: Boolean(rule.redeem_products),
      }
    case 'fixed_combo':
    default:
      return {}
  }
}

function buildAllowedServicesPayload(form) {
  if (!packageTypeUsesServices(form.type)) {
    return []
  }

  return form.allowed_services.map((row, index) => ({
    service_id: row.service_id,
    quantity: form.type === 'flexible_combo' ? 1 : Math.max(1, Number(row.quantity) || 1),
    sort: row.sort ?? (index + 1) * 10,
  }))
}

export function packageTypeUsesServices(type) {
  return ['fixed_combo', 'flexible_combo', 'session_pack', 'membership'].includes(type)
}

export function packageTypeUsesPickCount(type) {
  return type === 'flexible_combo'
}

export function packageTypeUsesCreditAmount(type) {
  return type === 'prepaid_wallet'
}

export function packageTypeUsesSessionCount(type) {
  return type === 'session_pack'
}

export function packageTypeUsesMembershipRule(type) {
  return type === 'membership'
}

export function packageTypeUsesGiftVoucherRule(type) {
  return type === 'gift_voucher'
}

export function packageTypeAllowsServiceQuantity(type) {
  return ['fixed_combo', 'membership'].includes(type)
}

/**
 * @param {ReturnType<typeof emptyPackageForm>} form
 * @param {number} serviceId
 * @param {string} serviceName
 */
export function addAllowedService(form, serviceId, serviceName) {
  if (form.allowed_services.some((row) => row.service_id === serviceId)) {
    return form.allowed_services
  }

  if (form.type === 'session_pack' && form.allowed_services.length >= 1) {
    return [{
      service_id: serviceId,
      service_name: serviceName,
      quantity: 1,
      sort: 10,
    }]
  }

  return [
    ...form.allowed_services,
    {
      service_id: serviceId,
      service_name: serviceName,
      quantity: 1,
      sort: (form.allowed_services.length + 1) * 10,
    },
  ]
}

/**
 * @param {ReturnType<typeof emptyPackageForm>} form
 * @param {number} serviceId
 */
export function removeAllowedService(form, serviceId) {
  return form.allowed_services.filter((row) => row.service_id !== serviceId)
}

/**
 * @param {ReturnType<typeof emptyPackageForm>} form
 * @param {number} serviceId
 * @param {string} field
 * @param {string | number | boolean} value
 */
export function updateAllowedService(form, serviceId, field, value) {
  return form.allowed_services.map((row) => (
    row.service_id === serviceId ? { ...row, [field]: value } : row
  ))
}

export function packageTypeLabel(type) {
  return posPackageTypeLabel(type)
}

function toAmount(value) {
  const amount = Number(value)
  if (!Number.isFinite(amount)) return '0.00'
  return amount.toFixed(2)
}
